import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, type Capability, type Me } from '@/api/client'

const ME_KEY = ['me'] as const

export function useMe() {
  return useQuery<Me | null>({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await api.get<Me>('/api/me')
      } catch (err) {
        // Not being logged in is a normal state, not an error to retry or surface.
        if (err instanceof ApiError && err.isUnauthorized) return null
        throw err
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  })
}

export function useLogin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { username: string; password: string; rememberMe: boolean }) =>
      api.post<void>('/api/auth/login', vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_KEY }),
  })
}

export function useLogout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<void>('/api/auth/logout'),
    onSuccess: () => qc.setQueryData(ME_KEY, null),
  })
}

/**
 * Capability checks hide UI the user cannot reach. This is a courtesy, not a security
 * boundary — every endpoint is gated server-side by the same LoginType policy map.
 */
export function useCan(capability: Capability): boolean {
  const { data } = useMe()
  return data?.capabilities.includes(capability) ?? false
}
